import {
  BlockKind,
  chunkPassage,
  findAll,
  isKeyChar,
  normalizeChar,
  readPassage,
  type KeyText,
  type PassageText,
  type TextBlock,
  type VisibleText,
} from './visible-text'

/** How far, in compared characters, a passage's next chunk may start after the previous one ends. */
const kChunkWindow = 20
const kUnspacedScript = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Lao}\p{Script=Khmer}\p{Script=Myanmar}]/u
const kMarkdownSyntax = /[*_#\\^~`>|]/u
const kWhitespace = /\s/u
const kTocPageNumber = /(\.{2,}|…|\t)\s*\d+\s*$/u

/**
 * A place a passage could be highlighted, as an inclusive range of compared-character indices,
 * with the passage pieces found there: the whole passage, or the chunks of a run.
 */
type Candidate = { start: number; end: number; pieces: { start: number; text: KeyText }[] }

/** Where one passage is highlighted: an inclusive range of indices into `chars`, and how much of it was found. */
export type PassagePlacement = {
  first: number
  last: number
  matchedChunks: number
  totalChunks: number
}

/**
 * Choose one place for each passage in the visible text, or null when it cannot be found.
 *
 * A passage that appears whole is placed where it appears. Otherwise its longest run of ten-word
 * chunks that follow each other closely is placed, gaps included. When a passage fits more than one
 * place, the place is chosen by narrowing in order until one is left: whole words over word
 * fragments, same capitalization and block kind (heading or body), closest to the passage's siblings
 * (where they were placed, or where they could go when none is placed yet), not a table-of-contents
 * entry, then the first.
 */
export function placePassages<Location>(text: VisibleText<Location>, passages: PassageText[]): (PassagePlacement | null)[] {
  if (!text.key) return passages.map(() => null)

  const narrowed = passages.map((passage) => narrowCandidates(text, passage, findCandidates(text.key, passage)))
  const chosen: (Candidate | null)[] = narrowed.map((candidates) => (candidates.length === 1 ? candidates[0] : null))
  narrowed.forEach((candidates, index) => {
    if (candidates.length < 2) return
    const placed = chosen.filter((candidate): candidate is Candidate => candidate !== null)
    // With no sibling placed yet, every place a sibling could go counts as where it matched.
    const siblings = placed.length > 0 ? placed : narrowed.filter((_other, otherIndex) => otherIndex !== index).flat()
    chosen[index] = chooseAmongAmbiguous(text, candidates, siblings)
  })

  return chosen.map((candidate, index) => {
    if (!candidate) return null
    const [first, last] = expandToVisibleRange(text, candidate, passages[index])
    const totalChunks = chunkPassage(passages[index]).length
    const isWhole = candidate.pieces.length === 1 && candidate.pieces[0].text === passages[index]
    return { first, last, matchedChunks: isWhole ? totalChunks : candidate.pieces.length, totalChunks }
  })
}

function findCandidates(key: string, passage: PassageText): Candidate[] {
  const whole = findAll(key, passage.key).map((start) => ({ start, end: start + passage.key.length - 1, pieces: [{ start, text: passage }] }))
  if (whole.length > 0) return whole
  return findBestChunkRuns(key, chunkPassage(passage))
}

/**
 * Start a run at every occurrence of every chunk, then extend it with each later chunk that
 * starts soon after the run ends. A chunk that cannot be found widens the window by its own
 * length, so one changed word does not break the run. Keep the runs with the most chunks.
 */
function findBestChunkRuns(key: string, chunks: KeyText[]): Candidate[] {
  const occurrences = chunks.map((chunk) => findAll(key, chunk.key))
  let best: Candidate[] = []
  chunks.forEach((chunk, chunkIndex) => {
    for (const start of occurrences[chunkIndex]) {
      const pieces = [{ start, text: chunk }]
      let end = start + chunk.key.length
      let missing = 0
      for (let next = chunkIndex + 1; next < chunks.length; next++) {
        const nextStart = occurrences[next].find((candidate) => candidate >= end && candidate <= end + missing + kChunkWindow)
        if (nextStart === undefined) {
          missing += chunks[next].key.length
          continue
        }
        pieces.push({ start: nextStart, text: chunks[next] })
        end = nextStart + chunks[next].key.length
        missing = 0
      }
      const run = { start, end: end - 1, pieces }
      if (best.length === 0 || pieces.length > best[0].pieces.length) best = [run]
      else if (pieces.length === best[0].pieces.length && !best.some((candidate) => candidate.start === start)) best.push(run)
    }
  })
  return best
}

/** Apply each rule only when it leaves at least one candidate. */
function keepIfAny(candidates: Candidate[], keep: (candidate: Candidate) => boolean): Candidate[] {
  const kept = candidates.filter(keep)
  return kept.length > 0 ? kept : candidates
}

function blockAt<Location>(text: VisibleText<Location>, candidate: Candidate): TextBlock | null {
  return text.chars[text.keyToChar[candidate.start]].block
}

function narrowCandidates<Location>(text: VisibleText<Location>, passage: PassageText, candidates: Candidate[]): Candidate[] {
  const wholeWords = keepIfAny(candidates, (candidate) => startsWord(text, candidate) && endsWord(text, candidate))
  return keepIfAny(wholeWords, (candidate) => {
    const sameKind = (blockAt(text, candidate)?.kind ?? BlockKind.BODY) === passage.blockKind
    const sameCase = candidate.pieces.every((piece) => text.casedKey.slice(piece.start, piece.start + piece.text.casedKey.length) === piece.text.casedKey)
    return sameKind && sameCase
  })
}

function chooseAmongAmbiguous<Location>(text: VisibleText<Location>, candidates: Candidate[], siblings: Candidate[]): Candidate {
  let remaining = candidates
  if (siblings.length > 0) {
    const distances = remaining.map((candidate) => Math.min(...siblings.map((other) => distanceBetween(candidate, other))))
    const closest = Math.min(...distances)
    remaining = remaining.filter((_candidate, index) => distances[index] === closest)
  }
  remaining = keepIfAny(remaining, (candidate) => !isTableOfContentsEntry(text, blockAt(text, candidate)))
  return remaining[0]
}

function distanceBetween(a: Candidate, b: Candidate): number {
  if (a.start > b.end) return a.start - b.end
  if (b.start > a.end) return b.start - a.end
  return 0
}

/** A table-of-contents line: styled as one, dot leaders before a page number, or body text that repeats a heading. */
function isTableOfContentsEntry<Location>(text: VisibleText<Location>, block: TextBlock | null): boolean {
  if (!block || block.kind === BlockKind.HEADING) return false
  if (block.isTableOfContents || kTocPageNumber.test(block.text)) return true
  const blockKey = readPassage(block.text).key
  if (!blockKey) return false
  return text.blocks.some((other) => other.kind === BlockKind.HEADING && readPassage(other.text).key === blockKey)
}

function startsWord<Location>(text: VisibleText<Location>, candidate: Candidate): boolean {
  const first = text.keyToChar[candidate.start]
  const previous = text.chars[first - 1]
  return !previous || !previous.isKey || isUnspaced(previous.char) || isUnspaced(text.chars[first].char)
}

function endsWord<Location>(text: VisibleText<Location>, candidate: Candidate): boolean {
  const last = text.keyToChar[candidate.end]
  const next = text.chars[last + 1]
  return !next || !next.isKey || isUnspaced(next.char) || isUnspaced(text.chars[last].char)
}

function isUnspaced(char: string): boolean {
  return kUnspacedScript.test(char)
}

/**
 * Turn a candidate into the range of visible characters to paint: widen a match that starts or
 * ends inside a word to the whole word, then take in the punctuation the passage starts or ends
 * with when the page has it right there.
 */
function expandToVisibleRange<Location>(text: VisibleText<Location>, candidate: Candidate, passage: PassageText): [number, number] {
  const { chars } = text
  let first = text.keyToChar[candidate.start]
  let last = text.keyToChar[candidate.end]
  while (chars[first - 1]?.isKey && !isUnspaced(chars[first - 1].char) && !isUnspaced(chars[first].char)) first--
  while (chars[last + 1]?.isKey && !isUnspaced(chars[last + 1].char) && !isUnspaced(chars[last].char)) last++

  const passageChars = [...passage.text]
  const firstKey = passageChars.findIndex(isKeyChar)
  const lastKey = passageChars.findLastIndex(isKeyChar)
  const leading = passageChars.slice(0, firstKey).filter(isPunctuationToMatch).map(normalizeChar)
  const trailing = passageChars
    .slice(lastKey + 1)
    .filter(isPunctuationToMatch)
    .map(normalizeChar)

  for (let index = leading.length - 1, position = first - 1; index >= 0; index--, position--) {
    while (chars[position]?.location && kWhitespace.test(chars[position].char)) position--
    if (!chars[position]?.location || chars[position].char !== leading[index]) break
    first = position
  }
  for (let index = 0, position = last + 1; index < trailing.length; index++, position++) {
    while (chars[position]?.location && kWhitespace.test(chars[position].char)) position++
    if (!chars[position]?.location || chars[position].char !== trailing[index]) break
    last = position
  }
  return [first, last]
}

function isPunctuationToMatch(char: string): boolean {
  return !kWhitespace.test(char) && !kMarkdownSyntax.test(char) && !isKeyChar(char)
}
